import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CoachingOrgAnalyticsSummary,
  CoachingOrgAnalyticsTimeSeries,
  CoachingOrgReportStatus,
  ReportReviewRequest
} from "../services/coaching-dtos";
import { getCoachingServices } from "../services/coaching-service-provider";
import { useWalkReadinessHold } from "../lib/walk-readiness";
import { coachingQueryKeys } from "./coaching-query-keys";
import { cacheReportMarkReviewedReadback } from "./coaching-write-truth-caches";
import { createIdempotencyKey } from "./useCoachingQueries";
const services = getCoachingServices();

type AnalyticsQuery = Record<string, string | number | boolean | null | undefined>;

/**
 * The org analytics rollup routes are ungated and HTTP-200 zero-filled (never
 * 503), so an EMPTY rollup is the honest "ingestion not live yet" state, NOT an
 * error. We disable retry so a transient failure surfaces fast, and the surface
 * treats `data === undefined` while `isError` as the same honest empty card
 * (never a 503 toast). The shapes are validated structurally by the live mapper.
 */
const analyticsQueryPolicy = {
  retry: false,
  staleTime: 30_000
};

export function useCoachingOrgAnalytics(query: AnalyticsQuery = {}) {
  const result = useQuery<CoachingOrgAnalyticsSummary>({
    queryKey: coachingQueryKeys.analytics.orgSummary(query),
    queryFn: () => services.analytics.orgSummary(query),
    ...analyticsQueryPolicy
  });
  useWalkReadinessHold(result.isLoading);
  return result;
}

export function useCoachingReportTimeSeries(query: AnalyticsQuery = {}) {
  const result = useQuery<CoachingOrgAnalyticsTimeSeries>({
    queryKey: coachingQueryKeys.analytics.timeSeries(query),
    queryFn: () => services.analytics.timeSeries(query),
    ...analyticsQueryPolicy
  });
  useWalkReadinessHold(result.isLoading);
  return result;
}

export function useCoachingReportStatus(query: AnalyticsQuery = {}) {
  const result = useQuery<CoachingOrgReportStatus>({
    queryKey: coachingQueryKeys.analytics.reportStatus(query),
    queryFn: () => services.analytics.reportStatus(query),
    ...analyticsQueryPolicy
  });
  useWalkReadinessHold(result.isLoading);
  return result;
}


/**
 * Phase 4 mark-reviewed mutation (org-admin spec §2.3a). Calls the backend
 * write route with a UUID Idempotency-Key; the backend re-applies the D-3 guard
 * (a company_admin reviewing a confidential report -> 403). On success it
 * invalidates the report list, the report detail, the dashboard, and the
 * report-status rollup so the follow-through queue and the donut both refresh.
 */
export function useReportMarkReviewedMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, decision }: { reportId: string; decision?: ReportReviewRequest["decision"] }) =>
      services.reports.markReviewed(reportId, { decision: decision ?? "reviewed" }, createIdempotencyKey(`reports-review:${reportId}`)),
    onSuccess: async (result) => {
      await cacheReportMarkReviewedReadback(queryClient, result);
    }
  });
}

/**
 * Imperative CSV export trigger. Returns the text body so a caller can stream a
 * download; the capability gate (exports:csv) governs whether the control is
 * rendered enabled at all.
 */
export function useReportExportCsv() {
  return useMutation({
    mutationFn: ({ exportType = "reports", query }: { exportType?: string; query?: AnalyticsQuery } = {}) =>
      services.reports.exportCsv(exportType, query)
  });
}
